// ─────────────────────────────────────────────────────────────────
// Glass-i — Stage 6B.8A-3C: simplified Ecommerce Product popup.
//
// Horizontal label layout matching the Glass screenshot reference.
// Maps onto inventory_items + ecommerce_product_profiles. Defaults
// keep the row hidden + draft + not visible online until the user
// explicitly opts in.
// ─────────────────────────────────────────────────────────────────

import { Col, Form, Input, InputNumber, Select } from 'antd';
import SimpleCreateModalShell from './SimpleCreateModalShell';
import {
  SIMPLE_KINDS,
  TAXABILITY_OPTIONS,
  ECOMMERCE_VISIBILITY_OPTIONS,
  ECOMMERCE_PUBLISH_STATUS_OPTIONS
} from '../../simpleCreateConstants';
import { ItemCategorySelect, UomSelect, GstRateSelect } from './simpleCreateFields';

const GST_RULE = {
  validator: (_, v) =>
    v == null || v === '' || (Number(v) >= 0 && Number(v) <= 100)
      ? Promise.resolve()
      : Promise.reject(new Error('GST must be between 0 and 100'))
};
const PRICE_RULE = {
  validator: (_, v) =>
    v == null || v === '' || Number(v) >= 0
      ? Promise.resolve()
      : Promise.reject(new Error('Selling Price cannot be negative'))
};
const MRP_RULE = {
  validator: (_, v) =>
    v == null || v === '' || Number(v) >= 0
      ? Promise.resolve()
      : Promise.reject(new Error('MRP cannot be negative'))
};
const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const SLUG_RULE = {
  validator: (_, v) =>
    !v || SLUG_RE.test(String(v).trim())
      ? Promise.resolve()
      : Promise.reject(new Error('Slug must be lowercase letters, digits and dashes only'))
};

export default function CreateEcommerceModal({
  open, onClose, onSaved, editRecord = null, initialValues: initialValuesProp = null,
  viewOnly = false
}) {
  const initialValues = (editRecord || viewOnly) && initialValuesProp
    ? initialValuesProp
    : {
        taxabilityType: 'taxable',
        visibility:     'hidden',
        publishStatus:  'draft',
        salesRate:      0
      };
  return (
    <SimpleCreateModalShell
      kind={SIMPLE_KINDS.ECOMMERCE}
      open={open}
      onClose={onClose}
      onSaved={onSaved}
      editRecord={editRecord}
      viewOnly={viewOnly}
      initialValues={initialValues}
      render={({ form }) => (
        <>
          {/* Row 1 — Product Name + Display Title */}
          <Col xs={24} md={12}>
            <Form.Item
              label="Product Name"
              name="itemName"
              required
              rules={[{ required: true, message: 'Product Name is required' }]}
            >
              <Input placeholder="e.g., 6mm Toughened Clear Glass Panel" autoFocus />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Display Title" name="ecommerceTitle">
              <Input placeholder="What customers see on the store" />
            </Form.Item>
          </Col>

          {/* Row 2 — Slug (full width) */}
          <Col xs={24}>
            <Form.Item label="Slug" name="ecommerceSlug" rules={[SLUG_RULE]}>
              <Input placeholder="e.g., toughened-glass-6mm" />
            </Form.Item>
          </Col>

          {/* Row 3 — Short Description (full width) */}
          <Col xs={24}>
            <Form.Item label="Short Description" name="shortDescription">
              <Input.TextArea rows={2} maxLength={300} placeholder="One- or two-line marketing summary" />
            </Form.Item>
          </Col>

          {/* Row 4 — Item Category + UOM */}
          <Col xs={24} md={12}>
            <Form.Item label="Item Category" name="itemCategoryId">
              <ItemCategorySelect placeholder="Select or type new…" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="UOM" name="defaultUomId"
              rules={[{ required: true, message: 'UOM is required' }]}>
              <UomSelect placeholder="Select UOM (SQM, PCS, KGS…)" />
            </Form.Item>
          </Col>

          {/* Row 5 — HSN/SAC + GST */}
          <Col xs={24} md={12}>
            <Form.Item label="HSN / SAC Code" name="hsncode">
              <Input maxLength={12} placeholder="e.g., 7005" />
            </Form.Item>
          </Col>
          <Form.Item name="gstRatePercent" hidden><Input /></Form.Item>
          <Col xs={24} md={12}>
            <Form.Item label="GST (%)" name="gstRateId"
              rules={[{ required: true, message: 'GST rate is required' }]}>
              <GstRateSelect form={form} />
            </Form.Item>
          </Col>

          {/* Row 6 — Selling Price + MRP */}
          <Col xs={24} md={12}>
            <Form.Item
              label="Selling Price"
              name="salesRate"
              rules={[{ required: true, message: 'Selling Price is required' }, PRICE_RULE]}
            >
              <InputNumber style={{ width: '100%' }} min={0} step={1} precision={2} placeholder="0.00" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="MRP" name="mrp" rules={[MRP_RULE]}>
              <InputNumber style={{ width: '100%' }} min={0} step={1} precision={2} placeholder="0.00" />
            </Form.Item>
          </Col>

          {/* Row 7 — Taxability + Visibility */}
          <Col xs={24} md={12}>
            <Form.Item label="Taxability" name="taxabilityType">
              <Select options={TAXABILITY_OPTIONS.map((o) => ({ value: o.value, label: o.label }))} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Visibility" name="visibility">
              <Select options={ECOMMERCE_VISIBILITY_OPTIONS.map((o) => ({ value: o.value, label: o.label }))} />
            </Form.Item>
          </Col>

          {/* Row 8 — Publish Status */}
          <Col xs={24} md={12}>
            <Form.Item label="Publish Status" name="publishStatus">
              <Select options={ECOMMERCE_PUBLISH_STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label }))} />
            </Form.Item>
          </Col>

          {/* Row 9 — Remarks (full width) */}
          <Col xs={24}>
            <Form.Item label="Remarks" name="remarks">
              <Input.TextArea rows={2} maxLength={500} placeholder="Add any additional notes here..." />
            </Form.Item>
          </Col>
        </>
      )}
    />
  );
}
